// @ts-nocheck
import { Fragment, lazy, Suspense } from 'react';
import { Helmet } from 'react-helmet-async';
import { BrowserRouter as Router, Redirect, Route, RouteComponentProps, RouteProps, Switch } from 'react-router-dom';
import { FillLoader, Sidebar } from '../components/shared';
import { BreadcrumbsArray } from '../components/shared/Breadcrumbs';
import AppConfig from '../config/AppConfig';
import Auth from '../containers/shared/auth/Auth';
import { AuthConsumer } from '../containers/shared/auth/AuthContext';
import Can from '../containers/shared/auth/Can';
import { adminRoutes } from './AdminRoutes';
import { funderRoutes } from './FunderRoutes';
import { institutionRoutes } from './InstitutionRoutes';
import { ADMIN_NAV_ITEMS, FUNDER_NAV_ITEMS, INSTITUTION_NAV_ITEMS, SP_NAV_ITEMS } from './NavItems';
import NoMatch from './NoMatch';
import { serviceProviderRoutes } from './ServiceProviderRoutes';
// The login screen stays in the loaded app. Logout sends people here, including while offline,
// and a separate chunk cannot be fetched once the network is off.
import LandingPage from '../containers/shared/auth/LandingPage';

// Async Routes
const AsyncForgotPassword = lazy(() => import('../containers/shared/auth/ForgotPassword'));
const AsyncResetPassword = lazy(() => import('../containers/shared/auth/ResetPassword'));
const AsyncSignupPage = lazy(() => import('../containers/shared/auth/Signup/SignUpPage'));
const AsyncValidate = lazy(() => import('../containers/shared/auth/ValidateUser'));
const AsyncCaptureForm = lazy(() => import('../containers/shared/admin/survey/CaptureSurvey'));
const AsyncRequestDatasest = lazy(() => import('../containers/shared/admin/workspace/RequestWorkspaceDataset'));
const AsyncDataTools = lazy(() => import('../containers/shared/dataTools/DataTools'));

type MatchParams = {
  id: string;
  otp: string;
};

type Props = RouteComponentProps<MatchParams>;

type AppNavigationProps = {
  isMobile: boolean;
  isTablet: boolean;
  isWeb: boolean;
};

type CustomRouteProps = RouteProps & { breadcrumbs: BreadcrumbsArray; component: any; path: string };

const AppNavigation = (props: AppNavigationProps) => {
  const { isMobile, isTablet, isWeb } = props;

  return (
    <Router>
      <Fragment>
        <Helmet defaultTitle={AppConfig.appName} titleTemplate={`${AppConfig.appName} | %s`} />
        <Suspense fallback={<FillLoader />}>
          <Auth>
            <AuthConsumer>
              {({ authenticated, acknowledgedDeclaration, user, defaultRedirectRoute, logout }) => {
                let defaultPortal = defaultRedirectRoute;

                return (
                  <Switch>
                    {/* Public Routes */}
                    <Route
                      exact={true}
                      path="/"
                      render={props => {
                        return (
                          <Can
                            logout={logout}
                            landingPage={true}
                            roles={user.roles}
                            plan={user.plan}
                            userAddOns={user.addOns}
                            action={'/:visit'}
                            yes={() => {
                              return <LandingPage {...props} />;
                            }}
                            no={() => {
                              return <Redirect to={{ pathname: defaultPortal, state: { from: props.location } }} />;
                            }}
                          />
                        );
                      }}
                    />
                    {/* Free Data Tools (IB-1818, DT-002-4). Deliberately in the public block with no
                        Can guard and no redirect: an anonymous visitor must reach it without being
                        sent to login. `authenticated` is passed through so the page can honour a
                        session where there is one — which is what decides whether the score is
                        stored against the workspace afterwards. */}
                    <Route
                      exact={true}
                      path="/data-tools"
                      render={props => {
                        return <AsyncDataTools {...props} authenticated={authenticated} />;
                      }}
                    />
                    <Route
                      exact={true}
                      path="/reset-password/:id/:otp"
                      render={props => {
                        return <AsyncResetPassword {...props} />;
                      }}
                    />

                    <Route
                      exact={true}
                      path="/validate/:id/:otp"
                      render={(props: Props) => {
                        return <AsyncValidate {...props} />;
                      }}
                    />
                    <Route
                      exact={true}
                      path="/forgot-password"
                      render={props => {
                        return <AsyncForgotPassword {...props} />;
                      }}
                    />

                    <Route
                      exact={true}
                      path="/signup"
                      render={props => {
                        return <AsyncSignupPage {...props} />;
                      }}
                    />

                    <Route
                      exact={true}
                      path="/capture/:id/"
                      render={props => {
                        return <AsyncCaptureForm {...props} />;
                      }}
                    />

                    <Route
                      exact={true}
                      path="/capture/:id/:userId"
                      render={props => {
                        return <AsyncCaptureForm {...props} />;
                      }}
                    />
                    <Route
                      exact={true}
                      path="/requestDataset/workspaces/:workspaceId/datasetsLinks/:linkId"
                      render={props => {
                        return <AsyncRequestDatasest {...props} />;
                      }}
                    />

                    {/* Admin Routes */}
                    {adminRoutes.map((route: CustomRouteProps, i) => {
                      const { breadcrumbs, component: Component, ...rest } = route;
                      return (
                        <Route
                          key={route.path}
                          {...rest}
                          render={props => (
                            <Can
                              logout={logout}
                              landingPage={false}
                              key={`${i}`}
                              roles={user.roles}
                              plan={user.plan}
                              userAddOns={user.addOns}
                              action={`${route.path}:visit`}
                              yes={() => {
                                return (
                                  <Sidebar
                                    portalType="admin"
                                    acknowledgedDeclaration={acknowledgedDeclaration}
                                    authenticated={authenticated}
                                    breadcrumbs={breadcrumbs}
                                    navItems={ADMIN_NAV_ITEMS}
                                    logout={logout}
                                    children={
                                      <Suspense fallback={<FillLoader />}>
                                        <Component
                                          portalType="admin"
                                          {...rest}
                                          isMobile={isMobile}
                                          isTablet={isTablet}
                                          isWeb={isWeb}
                                        />
                                      </Suspense>
                                    }
                                  />
                                );
                              }}
                              no={() => {
                                return <Redirect to={{ pathname: '/', state: { from: props.location } }} />;
                              }}
                            />
                          )}
                        />
                      );
                    })}

                    {/* Service Provider Routes */}
                    {serviceProviderRoutes.map((route: CustomRouteProps, i) => {
                      const { breadcrumbs, component: Component, ...rest } = route;
                      return (
                        <Route
                          key={route.path}
                          {...rest}
                          render={props => {
                            return (
                              <Can
                                logout={logout}
                                landingPage={false}
                                key={`${i}`}
                                roles={user.roles}
                                plan={user.plan}
                                userAddOns={user.addOns}
                                action={`${route.path}:visit`}
                                data={{ props, user }}
                                yes={() => {
                                  return (
                                    <Sidebar
                                      portalType="service-provider"
                                      acknowledgedDeclaration={acknowledgedDeclaration}
                                      authenticated={authenticated}
                                      breadcrumbs={breadcrumbs}
                                      navItems={SP_NAV_ITEMS()}
                                      userOrganisationId={user && user.userOrganisation && user.userOrganisation.id}
                                      logout={logout}
                                      children={
                                        <Suspense fallback={<FillLoader />}>
                                          <Component
                                            portalType="service-provider"
                                            {...rest}
                                            isMobile={isMobile}
                                            isTablet={isTablet}
                                            isWeb={isWeb}
                                          />
                                        </Suspense>
                                      }
                                    />
                                  );
                                }}
                                no={() => {
                                  return <Redirect to={{ pathname: '/', state: { from: props.location } }} />;
                                }}
                              />
                            );
                          }}
                        />
                      );
                    })}

                    {/* Funder Routes */}
                    {funderRoutes.map((route: CustomRouteProps, i) => {
                      const { breadcrumbs, component: Component, ...rest } = route;
                      return (
                        <Route
                          key={route.path}
                          {...rest}
                          render={props => (
                            <Can
                              logout={logout}
                              landingPage={false}
                              key={`${i}`}
                              roles={user.roles}
                              plan={user.plan}
                              userAddOns={user.addOns}
                              action={`${route.path}:visit`}
                              yes={() => {
                                return (
                                  <Sidebar
                                    portalType="funder"
                                    acknowledgedDeclaration={acknowledgedDeclaration}
                                    authenticated={authenticated}
                                    breadcrumbs={breadcrumbs}
                                    navItems={FUNDER_NAV_ITEMS()}
                                    userOrganisationId={user && user.userOrganisation && user.userOrganisation.id}
                                    logout={logout}
                                    children={
                                      <Suspense fallback={<FillLoader />}>
                                        <Component
                                          portalType="funder"
                                          {...rest}
                                          isMobile={isMobile}
                                          isTablet={isTablet}
                                          isWeb={isWeb}
                                        />
                                      </Suspense>
                                    }
                                  />
                                );
                              }}
                              no={() => {
                                return <Redirect to={{ pathname: '/', state: { from: props.location } }} />;
                              }}
                            />
                          )}
                        />
                      );
                    })}

                    {/* Institution Routes */}
                    {institutionRoutes.map((route: CustomRouteProps, i: number) => {
                      const { breadcrumbs, component: Component, ...rest } = route;
                      return (
                        <Route
                          key={route.path}
                          {...rest}
                          render={props => {
                            return (
                              <Can
                                logout={logout}
                                landingPage={false}
                                key={`${i}`}
                                roles={user.roles}
                                plan={user.plan}
                                userAddOns={user.addOns}
                                action={`${route.path}:visit`}
                                data={{ props, user }}
                                yes={() => {
                                  return (
                                    <Sidebar
                                      portalType="institution"
                                      acknowledgedDeclaration={acknowledgedDeclaration}
                                      authenticated={authenticated}
                                      breadcrumbs={breadcrumbs}
                                      navItems={INSTITUTION_NAV_ITEMS()}
                                      userOrganisationId={user && user.userOrganisation && user.userOrganisation.id}
                                      logout={logout}
                                      isMobile={isMobile}
                                      isTablet={isTablet}
                                      isWeb={isWeb}
                                      children={
                                        <Suspense fallback={<FillLoader />}>
                                          <Component
                                            portalType="institution"
                                            {...rest}
                                            isMobile={isMobile}
                                            isTablet={isTablet}
                                            isWeb={isWeb}
                                          />
                                        </Suspense>
                                      }
                                    />
                                  );
                                }}
                                no={() => {
                                  return <Redirect to={{ pathname: '/', state: { from: props.location } }} />;
                                }}
                              />
                            );
                          }}
                        />
                      );
                    })}
                    {/* Other Routes */}
                    <Route component={NoMatch} />
                  </Switch>
                );
              }}
            </AuthConsumer>
          </Auth>
        </Suspense>
      </Fragment>
    </Router>
  );
};

export default AppNavigation;
